import { useParameterStore } from "@/frontend/store/ParameterStore";
import { I18n } from "@/i18n/I18n";
import { Parameter } from "@/backend/models/parameter";
import { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { BasicButton } from "../../buttons/BasicButton";
import { Plus } from "lucide-react";
import { ButtonParent } from "../../buttons/ButtonParent";

type Value = { startingValue: string, endingValue: string, stepValue: string, numberOfSteps: string, keyName: string };
type ValuesMap = { [key: string]: Value };

export function GenerateObjectValues(props: { parameter: Parameter }) {
  const { parameter } = props;
  const originalStartingValues: ValuesMap = {};
  parameter.propertyKeys.forEach(key => {
    originalStartingValues[key.keyId] = { startingValue: '0', endingValue: '0', stepValue: '0', numberOfSteps: '0', keyName: key.key };
  });
  const [values, setValues] = useState(originalStartingValues);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEditValues, setShowEditValues] = useState(false);
  const generateParameterValues = useParameterStore(state => state.generateObjectParameterValues);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const startingValues: ValuesMap = {};
    parameter.propertyKeys.forEach(key => {
      startingValues[key.keyId] = { startingValue: '0', endingValue: '0', stepValue: '0', numberOfSteps: '0', keyName: key.key };
    });
    setValues(startingValues);
  }, [parameter]);

  const onConfirm = async () => {
    if (!Object.values(values).every((val, i, arr) => val.numberOfSteps === arr[0].numberOfSteps )) {
      setError(I18n.get('numberOfValuesError'));
      return;
    }
    setError(null);
    setIsGenerating(true);
    await generateParameterValues({
      parameterId: parameter.id,
      keyMap: values,
    });
    setIsGenerating(false);
    setShowEditValues(false);
  };

  const setValuesWrapper = (keyId: string, value: Value) => {
    const numberOfSteps = Math.ceil((parseFloat(value.endingValue) - parseFloat(value.startingValue)) / parseFloat(value.stepValue) + 1);

    const newValues = { ...values, [keyId]: { ...value, numberOfSteps: numberOfSteps.toString() } };
    setValues(newValues);
  };

  return (
    <>
      <ButtonParent
        className='fusa-btn-small-with-icon fusa-btn-primary'
        title={I18n.get('plusRangedValues')}
        onClick={() => setShowEditValues(true)}
      >
        <Plus size={16} />
        <span>{I18n.get('addRangedValues')}</span>
      </ButtonParent>
      <Modal size='sm' show={showEditValues} onHide={() => setShowEditValues(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('addRangedValues')}</Modal.Title>
        </Modal.Header >
        <Modal.Body>

          { Object.keys(values).map(key => <GenerateValuesBody key={key} keyName={values[key].keyName} setValue={(value: Value) => setValuesWrapper(key, value)} value={values[key]} />) }
          { error && <div className='text-danger'>{error}</div> }
          <BasicButton
            className='btn btn-sm btn-primary'
            label={I18n.get('confirm')}
            onClick={onConfirm}
            processing={isGenerating}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}

function GenerateValuesBody(props: { keyName: string, setValue: (value: Value) => void, value: Value }) {
  const { keyName, setValue, value } = props;

  return (
    <div className='pb-3'>
      <h6>{I18n.get('rangeValuesFor', { keyName })}</h6>
      <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('startingValue')}</Form.Label>
      <Form.Control
        size='sm'
        type='number'
        aria-label={I18n.get('enterField', { field: I18n.get('startingValue') })}
        defaultValue={value.startingValue}
        onBlur={(event) => setValue({ ...value, startingValue: event.target.value })}
      />
      <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('endingValue')}</Form.Label>
      <Form.Control
        size='sm'
        type='number'
        aria-label={I18n.get('enterField', { field: I18n.get('endingValue') })}
        defaultValue={value.endingValue}
        onBlur={(event) => setValue({ ...value, endingValue: event.target.value })}
      />
      <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('increment')}</Form.Label>
      <Form.Control
        size='sm'
        type='number'
        aria-label={I18n.get('enterField', { field: I18n.get('increment') })}
        defaultValue={value.stepValue}
        onBlur={(event) => setValue({ ...value, stepValue: event.target.value })}
        className='mb-2'
      />
      <Form.Label className='col-form-label col-form-label-sm'>{I18n.get('numberOfValues')}</Form.Label>
      <Form.Control
        size='sm'
        type='number'
        aria-label={I18n.get('enterField', { field: I18n.get('numberOfValues') })}
        value={value.numberOfSteps}
        className='mb-2'
        disabled
      />
    </div>
  );
}
